// Account-scoped server state. A failed refresh is never a free-tier decision or
// a zero balance; callers must surface the failure before economic mutations.
export function createPlayerEconomy({ client, assertIdentity = () => {} }) {
  let state = null, error = null, pending = null, stopped = false, timer, aiError = null
  const listeners = new Set(), purchaseKeys = new Map()
  let rewardRecovery = null
  const emit = () => { for (const fn of listeners) { try { fn(state) } catch {} } }
  const assert = () => { assertIdentity(); if (stopped) throw new Error('This account is no longer active.') }
  async function refresh() {
    assert()
    if (pending) return pending
    const owner = rewardOwner()
    pending = (async () => {
      try {
        const [overview, entitlements, catalog] = await Promise.all([client.economy(), client.entitlements(), client.shopItems()])
        assertRewardOwner(owner)
        state = { ...overview, entitlements, catalog }; error = null; emit()
        return state
      } catch (failure) {
        if (!stopped && owner.id === String(client.user?.id ?? '') && owner.version === client.identityVersion) { error = failure; emit() }
        throw failure
      }
      finally { pending = null }
    })()
    return pending
  }
  async function mutate(action) {
    assert(); await refresh(); assert()
    const result = await action(); assert()
    await refresh()
    return result
  }
  const rewardOwner = () => ({ id: String(client.user?.id ?? ''), version: client.identityVersion })
  function assertRewardOwner(owner) {
    assert()
    if (owner.id !== String(client.user?.id ?? '') || owner.version !== client.identityVersion) throw new Error('This ad offer belongs to an earlier account session.')
  }
  async function rewardRead(action) {
    assert(); const owner = rewardOwner()
    const result = await action(); assertRewardOwner(owner)
    return result
  }
  async function issueReward(options) {
    assert(); const owner = rewardOwner()
    if (rewardRecovery && (rewardRecovery.owner !== owner.id || rewardRecovery.version !== owner.version)) throw new Error('This ad offer belongs to an earlier account session.')
    if (!rewardRecovery?.key) rewardRecovery = { owner: owner.id, version: owner.version, key: globalThis.crypto.randomUUID(), offer: null, receipt: null }
    const result = await rewardRead(() => client.issueAdReward(rewardRecovery.key, options))
    if (typeof result?.offer_id !== 'string') throw new Error('The server returned an invalid ad offer.')
    rewardCreditAmount(result, { newOffer: !result.legacy })
    rewardRecovery.offer = structuredClone(result)
    return result
  }
  async function rewardStatus(id, options) {
    const result = await rewardRead(() => client.adRewardStatus(id, options))
    if (result?.offer_id !== id || typeof result.status !== 'string') throw new Error('The server returned an invalid ad receipt.')
    if (rewardRecovery) rewardRecovery.receipt = structuredClone(result)
    return result
  }
  async function refreshRewardWallet(receipt) {
    assert(); const owner = rewardOwner()
    if (receipt?.status !== 'granted' || typeof receipt.offer_id !== 'string') throw new Error('A server grant acknowledgement is required.')
    rewardCreditAmount(receipt, { granted: true })
    // A wallet read started before acknowledgement may be stale. Keep its own
    // error observable to its caller, then perform a fresh authoritative read.
    if (pending) await pending.catch(() => {})
    assertRewardOwner(owner)
    await refresh(); assertRewardOwner(owner)
    rewardRecovery = null
    return state
  }
  const update = () => {
    clearTimeout(timer)
    timer = setTimeout(async () => {
      // A refresh started before the reply can contain the old wallet. Follow
      // it with a new read instead of deduplicating away the completed charge.
      if (pending) await pending.catch(() => {})
      if (!stopped) await refresh().catch(() => {})
    }, 250)
  }
  const unsubscribe = client.onAiUsage?.((status, code, detail) => {
    const limitMessages = {
      daily_limit: 'Your daily AI request limit has been reached. Try again tomorrow.',
      operation_limit: 'This feature has reached its daily allowance. Try again tomorrow.',
      request_rate_limit: 'Too many AI requests in a short time. Wait a minute and try again.',
      concurrency_limit: 'Your previous AI replies are still finishing. Please wait before retrying.',
      billing_busy: 'Your previous AI reply is still finishing. Please wait before retrying.',
      service_busy: 'Meo’s AI is busy. Please wait before retrying.',
    }
    const creditMessage = ['insufficient_credits', 'INSUFFICIENT_CREDITS', 'query_budget_exceeded'].includes(code) && typeof detail?.message === 'string'
      ? detail.message : 'Your credit balance is below zero. Add credits to continue.'
    aiError = status === 402 ? creditMessage
      : status === 429 ? limitMessages[code] || 'Meo’s AI is busy. Please wait before retrying.'
      : status === 503 && code === 'upstream_unavailable' ? 'The AI service is unavailable. No credits were charged. Please try again shortly.'
      : status === 504 ? 'The AI service timed out. No credits were charged. Please try again shortly.' : null
    update()
  })
  globalThis.addEventListener?.('focus', update)
  return {
    get state() { return state }, get error() { return error }, get aiError() { return aiError }, refresh,
    get rewardRecovery() { return rewardRecovery ? structuredClone(rewardRecovery) : null },
    rewardAvailability: options => rewardRead(() => client.adRewards(options)),
    issueReward, rewardStatus, refreshRewardWallet,
    cancelReward: (id, options) => rewardRead(() => client.cancelAdReward(id, options)),
    clearRewardRecovery() { assert(); rewardRecovery = null },
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn) },
    claimDaily: () => mutate(() => client.claimDailyReward()),
    claimReferral: code => mutate(() => client.claimReferral(code)),
    async purchase(sku) {
      if (!purchaseKeys.has(sku)) purchaseKeys.set(sku, globalThis.crypto.randomUUID())
      // Retain the key even after a lost response or refresh failure.
      return mutate(() => client.purchaseCosmetic(sku, purchaseKeys.get(sku)))
    },
    activate: ids => mutate(() => client.activateCompanions(ids)),
    owns: item => !!state?.entitlements.cosmetics.some(owned => owned.sku === item.sku),
    stop() { stopped = true; clearTimeout(timer); unsubscribe?.(); listeners.clear(); globalThis.removeEventListener?.('focus', update) },
  }
}

// Asset references are catalog IDs, not arbitrary asset URLs from a purchase.
export function resolveCosmetic(item, { scenes = [], outfits = [] } = {}) {
  return item.kind === 'home' ? scenes.find(scene => scene.id === item.asset_ref)
    : item.kind === 'outfit' ? outfits.find(outfit => outfit.id === item.asset_ref) : null
}

export function aiCostText(state) {
  if (!state) return 'Checking AI access…'
  if (state.policy.demo_mode) return 'AI conversations are free during the demo.'
  if (state.policy.billing_mode === 'usage') return 'AI actions use credits after each reply. A reply can take your balance below zero; add credits before your next request. Failed requests use no credits.'
  const price = state.policy.prices.chat
  return price ? `Chat: ${price.credits} credits per request${price.paid_only ? ' · purchased credits only' : ''}. Voice and other AI actions have separate prices.` : 'Chat is unavailable until its price is configured.'
}

export function promotionText(state) {
  const promotion = state?.promotion
  if (!promotion) return ''
  const offer = `${promotion.discount_percent}% fewer credits on Claude.`
  if (!promotion.ends_at) return offer
  const end = new Date(promotion.ends_at)
  if (!Number.isFinite(end.getTime())) return offer
  return `${offer} Ends ${end.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}.`
}

export const formatCredits = value => new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 }).format(value)
export const walletCreditText = state => `${formatCredits((state.wallet.total || 0) + (state.wallet.reserved || 0))} credits`

export const REVENUE_REWARD_VERSION = 'revenue-minimum-v1'
// Exact decimal credit text is the authoritative transport. Numeric credits is
// a compatibility/display field; neither field can authorize a browser grant.
export function rewardCreditAmount(value, { newOffer = false, granted = false } = {}) {
  if (value?.reward_version === REVENUE_REWARD_VERSION) {
    const text = value.credit_amount
    if (typeof text !== 'string' || text.length > 32 || !/^(0|[1-9][0-9]*)(\.[0-9]{1,4})?$/.test(text)) throw new Error('The server returned an invalid ad credit amount.')
    const [whole, fractional = ''] = text.split('.')
    const amount = BigInt(whole) * 10000n + BigInt(fractional.padEnd(4, '0'))
    if (amount > BigInt(Number.MAX_SAFE_INTEGER) || typeof value.credits !== 'number' || !Number.isFinite(value.credits) || value.credits !== Number(text) || ((newOffer || granted) && amount < 30000n)) throw new Error('The server returned an invalid ad credit amount.')
    if (newOffer && !['known', 'estimated', 'cold_start'].includes(value.quote_state)) throw new Error('The server returned an invalid ad quote.')
    return text
  }
  // Read-only compatibility for historical fixed +1 grants and pending data.
  if (!newOffer && !value?.reward_version && (value?.credits === 1 || (!granted && value?.credits === 0))) return String(value.credits)
  throw new Error(granted ? 'The server returned an invalid ad award.' : 'The server returned an unsupported ad reward policy.')
}
